// Quién va a la Oficina Virtual desde el header, y quién sigue en el panel legacy.
//
// La regla de siempre: un plan con `self_service` (salvo el vendedor de una inmobiliaria)
// y todo Colombia. Paraguay suma a sus PRIVADOS (INF-1894): la OV los deja entrar desde
// INF-488 (la puerta es INF-486). Sus inmobiliarias sin `self_service` siguen en el legacy,
// y por eso la excepción vale sólo para `individual`, no para la regla común.
//
// ⚠️ La OV decide si deja entrar al privado con dos interruptores propios:
// `config/ov_privados.php` de IC2 y `VITE_OV_PRIVADOS_ENABLED` de la OV (environment de
// PY). Esta lista es un tercero, que decide si el buyer lo manda. Apagar PY desde
// cualquiera de aquéllos exige sacarlo también de acá: si no, el privado entra a la OV y
// la OV lo devuelve al legacy.
const COUNTRIES_WITH_PRIVATES_IN_OV = ["PY"];

type Me = {
	individual?: boolean;
	__typename?: string;
	plan?: { self_service?: boolean } | null;
} | null | undefined;

export const isPrivateWithOV = (me: Me, country_code: string): boolean =>
	!!me?.individual && COUNTRIES_WITH_PRIVATES_IN_OV.includes(country_code);

export const goesToOV = (me: Me, country_code: string): boolean =>
	(!!me?.plan?.self_service && me?.__typename !== "Seller") ||
	country_code === "CO" ||
	isPrivateWithOV(me, country_code);

// Logueado, el privado ve el desplegable «Dueño Vende / Alquila…», que lleva al alta del
// legacy. Donde publica en la OV —y en ce3— ve el botón directo.
export const privateSeesPublishDropdown = (me: Me, country_code: string): boolean =>
	!!me?.individual &&
	country_code !== "ce3" &&
	country_code !== "CO" &&
	!isPrivateWithOV(me, country_code);

// El botón de publicar abre en una pestaña nueva en CO y ce3, y también para el privado de
// PY (laudo de jhon, 2026-09-28), igual que los links de su menú.
export const publishOpensInNewTab = (me: Me, country_code: string): boolean =>
	country_code === "ce3" || country_code === "CO" || isPrivateWithOV(me, country_code);

// Sin sesión, «Publica tu propiedad» es el botón que abre el login y después publica, como en
// CO y ce3. Donde el privado todavía no publica en la OV sigue el desplegable «Inmobiliaria /
// Dueño Vende / Alquila…», que lleva a la landing `/publicar` del legacy. PY dejó esa landing
// en INF-2017, y por eso va atado a la misma lista: sin la OV no hay a dónde mandarlo.
export const loggedOutSeesPublishButton = (country_code: string): boolean =>
	country_code === "ce3" ||
	country_code === "CO" ||
	COUNTRIES_WITH_PRIVATES_IN_OV.includes(country_code);

// A dónde lleva el botón de publicar. Antes del login `goesToOV` sólo sabe el país: en PY daría
// el alta del legacy aunque quien entra sea un privado, y por eso PY lo resuelve después (abajo).
export const publishUrl = (me: Me, country_code: string, main_domain: string, ov_path: string): string => {
	if (goesToOV(me, country_code)) return `${ov_path}publicar`;
	if (country_code === "ce3") return "https://www.infocasas.com.uy/publicar-alquiler-temporario";
	return `https://${main_domain}/sitio/index.php?mid=propiedades&func=add`;
};

// Sin sesión, el destino de PY depende de quién se loguea —el privado va a la OV, la
// inmobiliaria sin `self_service` al legacy—, así que se decide DESPUÉS del login con el `me`
// recién traído. En los demás el login no cambia el destino y se usa el de antes (INF-2017).
export const publishResolvesAfterLogin = (country_code: string): boolean =>
	COUNTRIES_WITH_PRIVATES_IN_OV.includes(country_code);

// Sin `me` después del login —el modal resolvió sin sesión, como en «olvidé mi contraseña», o
// la query falló— va a la OV, que pide el login y despacha por rol: nunca al alta del legacy,
// que es justo lo que INF-2017 saca de PY.
export const publishUrlAfterLogin = (me: Me, country_code: string, main_domain: string, ov_path: string): string =>
	me ? publishUrl(me, country_code, main_domain, ov_path) : `${ov_path}publicar`;
